import type { OwnerResource } from "../../owner";

/**
 * Types for the marketing Gutschein-Pools (backend modules/Marketing,
 * CouponPoolController).
 *
 * Conventional resource: /api/marketing-coupon-pool (list, show, store,
 * update, destroy, new-form). Action endpoints:
 * - POST /marketing-coupon-pool/{id}/import — CSV from the shop (field `file`);
 * - GET  /marketing-coupon-pool/{id}/codes  — codes of the pool;
 * - GET  /marketing-coupon-pool/{id}/imports — import history;
 * - POST /marketing-coupon-code/{id}/void   — mark a free code as void.
 *
 * Codes are generated by the shop and uploaded here; the ERP voucher module
 * is not involved (DEC-031, DEC-036).
 */

export enum CouponCodeStatusEnum {
  Available = "available",
  Reserved = "reserved",
  Void = "void",
}

/** Morph alias of the record a code is reserved for. */
export enum CouponReservedForTypeEnum {
  CampaignExecution = "campaign-execution",
  RecommendationCampaignCustomer = "recommendation-campaign-customer",
}

export interface MarketingCouponPoolCodeCounts {
  total: number;
  available: number;
  reserved: number;
  void: number;
}

/**
 * Only for a pool an active or paused birthday campaign sends from; null
 * otherwise. `covers_days` null — the codes last for the whole horizon.
 * `campaign_status` null — forecast asked for an unused pool (?forecast=always).
 */
export interface MarketingCouponPoolForecast {
  available: number;
  covers_days: number | null;
  upcoming: number;
  horizon_days: number;
  campaign_status: "active" | "paused" | null;
}

export interface MarketingCouponPoolResource {
  id: string;
  owner_id: string;
  name: string;
  shop_series_reference: string | null;
  discount_label: string | null;
  valid_from: string | null;
  valid_until: string | null;
  is_active: boolean;
  note: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  codes?: MarketingCouponPoolCodeCounts;
  forecast?: MarketingCouponPoolForecast | null;
  owner?: OwnerResource;
}

export interface MarketingCouponCodeResource {
  id: string;
  pool_id: string;
  code: string;
  status: CouponCodeStatusEnum;
  customer_id: string | null;
  /** include=customer */
  customer?: { id: string; number: string | null; name: string | null } | null;
  reserved_for_type: CouponReservedForTypeEnum | null;
  reserved_for_id: string | null;
  /** Campaign that gave the code out; null for a free code. */
  reserved_for_campaign: {
    type: "anlass" | "recommendation";
    id: string;
    name: string;
  } | null;
  reserved_at: string | null;
  import_id: string | null;
  created_at: string;
}

export interface MarketingCouponImportResource {
  id: string;
  pool_id: string;
  user_id: string | null;
  original_filename: string | null;
  rows_total: number;
  imported: number;
  duplicates_in_file: number;
  already_exists: number;
  invalid: number;
  /** Only in the response to the import itself. */
  invalid_lines?: number[];
  /** First line taken as a header and skipped; only in the response to the import itself. */
  skipped_header?: string | null;
  created_at: string;
}
